import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowLeft, ChevronRight, PackageSearch, RefreshCcw, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import UserNavbar from "../../components/UserNavbar";

interface OrderItem {
  product_id?: string;
  product_name?: string;
  name?: string;
  image?: string;
  quantity?: number;
  price?: number;
}

interface Order {
  _id?: string;
  delivery_date?: string;
  createdAt?: string;
  date?: string;
  status?: string;
  total_amount?: number;
  grand_total?: number;
  total?: number;
  subtotal?: number;
  items?: OrderItem[];
  product_id?: string;
  product_name?: string;
  image?: string;
  quantity?: number;
  price?: number;
}

const dateKey = (value?: string) => {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : value || "unknown";
};

const displayDate = (value?: string) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "short", year: "numeric" });
};

const amount = (order: Order) =>
  Number(order.total_amount ?? order.grand_total ?? order.total ?? order.subtotal ?? 0);

export default function OrderHistory() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}order-history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const raw = response?.data?.data || response?.data?.orders || [];
      const normalized = Array.isArray(raw)
        ? raw.flatMap((group: any) =>
            Array.isArray(group?.orders)
              ? group.orders.map((order: Order) => ({
                  ...order,
                  delivery_date: order.delivery_date || group.delivery_date,
                }))
              : [group]
          )
        : [];

      setOrders(normalized);
    } catch (error) {
      console.error("Unable to load order history", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const groups = useMemo(() => {
    const map = new Map<string, any>();

    orders.forEach((order) => {
      const rawDate = order.delivery_date || order.createdAt || order.date;
      const key = dateKey(rawDate);
      if (!map.has(key)) {
        map.set(key, {
          key,
          date: rawDate,
          orders: [],
          totalItems: 0,
          totalAmount: 0,
        });
      }

      const group = map.get(key);
      group.orders.push(order);
      group.totalAmount += amount(order);
      group.totalItems += Array.isArray(order.items) && order.items.length
        ? order.items.length
        : 1;
    });

    return Array.from(map.values()).sort((a, b) =>
      String(b.key).localeCompare(String(a.key))
    );
  }, [orders]);

  return (
    <>
      <UserNavbar />
      <main className="min-h-screen bg-slate-50 pt-40 px-4 pb-10">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="w-11 h-11 rounded-full bg-white shadow-sm flex items-center justify-center"
                aria-label="Go back"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-bold">My Orders</h1>
                <p className="text-slate-500">Order history grouped by delivery date.</p>
              </div>
            </div>

            <button
              onClick={fetchOrders}
              className="rounded-xl bg-violet-600 text-white px-4 py-2 flex items-center gap-2"
            >
              <RefreshCcw size={17} />
              Refresh
            </button>
          </div>

          {loading ? (
            <div className="bg-white rounded-3xl p-16 text-center">Loading orders...</div>
          ) : groups.length === 0 ? (
            <div className="bg-white rounded-3xl p-16 text-center">
              <PackageSearch size={44} className="mx-auto text-slate-400" />
              <h2 className="text-xl font-semibold mt-4">No orders found</h2>
            </div>
          ) : (
            <div className="space-y-4">
              {groups.map((group) => (
                <button
                  key={group.key}
                  onClick={() => setSelected(group)}
                  className="w-full text-left bg-white rounded-2xl border border-slate-200 p-5 hover:border-violet-300 transition"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h2 className="font-semibold text-lg">{displayDate(group.date)}</h2>
                      <div className="mt-3 flex gap-8 text-sm text-slate-500">
                        <span>Items: <strong className="text-slate-900">{group.totalItems}</strong></span>
                        <span>Total: <strong className="text-slate-900">₹{group.totalAmount.toFixed(2)}</strong></span>
                      </div>
                    </div>
                    <ChevronRight className="text-violet-600" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </main>

      {selected && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-2xl font-bold">Order Details</h2>
                <p className="text-slate-500">{displayDate(selected.date)}</p>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 rounded-full hover:bg-slate-100">
                <X />
              </button>
            </div>

            <div className="space-y-4">
              {selected.orders.map((order: Order, orderIndex: number) => {
                const items = Array.isArray(order.items) && order.items.length
                  ? order.items
                  : [{
                      product_id: order.product_id,
                      product_name: order.product_name,
                      image: order.image,
                      quantity: order.quantity,
                      price: order.price,
                    }];

                return (
                  <div key={order._id || orderIndex} className="border border-slate-200 rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold">{order.status || "Confirmed"}</span>
                      <span className="font-bold">₹{amount(order).toFixed(2)}</span>
                    </div>

                    <div className="space-y-3">
                      {items.map((item: OrderItem, itemIndex: number) => (
                        <div key={item.product_id || itemIndex} className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-xl bg-slate-100 overflow-hidden shrink-0">
                            {item.image ? (
                              <img src={item.image} alt={item.product_name || item.name || "Product"} className="w-full h-full object-cover" />
                            ) : null}
                          </div>
                          <div className="flex-1">
                            <div className="font-medium">{item.product_name || item.name || "Product"}</div>
                            <div className="text-sm text-slate-500">Qty: {item.quantity || 1}</div>
                          </div>
                          <div className="font-semibold">₹{Number(item.price || 0).toFixed(2)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
